/**
 * World countries + curated city lists.
 *
 * COUNTRIES — standard ISO 3166-1 list (192 UN members + common dependencies).
 * Pakistan listed first; rest alphabetical.
 *
 * CITIES_BY_COUNTRY — keyed by country name (case-sensitive — same string we
 * store on the user). Each entry is a curated list of major cities. For
 * countries not in this map, the city dropdown falls back to "Other" only
 * (lets users type any city).
 *
 * Pakistan cities come from `pakistanCities.js` (200+ entries). For overseas
 * markets we ship the top 30-50 cities — enough for the diaspora majority +
 * panel attribution + ad-targeting.  Users always have an "Other" option at
 * the top of the dropdown so the system never blocks a custom value.
 */
import { ALL_PAKISTAN_CITIES } from "./pakistanCities";

// Standard country list — Pakistan first, then alphabetical.
export const COUNTRIES = [
  "Pakistan",
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
  "Cambodia", "Cameroon", "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
  "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Fiji", "Finland", "France",
  "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hong Kong", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast",
  "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Macau", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
  "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
  "Oman",
  "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar",
  "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
  "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Yemen",
  "Zambia", "Zimbabwe",
];

// Curated city lists for the countries the Pakistani diaspora most commonly
// settles in. Each list ~25-50 cities — enough coverage for ~95% of users,
// with "Other" always available at the top of the dropdown for edge cases.
const CITIES = {
  "Pakistan": ALL_PAKISTAN_CITIES,
  "United Kingdom": [
    "London", "Manchester", "Birmingham", "Leeds", "Glasgow", "Sheffield", "Bradford", "Liverpool", "Edinburgh", "Bristol",
    "Cardiff", "Coventry", "Leicester", "Nottingham", "Newcastle upon Tyne", "Belfast", "Brighton", "Hull", "Plymouth", "Stoke-on-Trent",
    "Wolverhampton", "Derby", "Southampton", "Portsmouth", "Aberdeen", "Dundee", "Swansea", "Reading", "Luton", "Slough",
    "Cambridge", "Oxford", "Milton Keynes", "Northampton", "York", "Bournemouth", "Norwich", "Peterborough", "Sunderland", "Middlesbrough",
  ],
  "United States": [
    "New York City", "Los Angeles", "Chicago", "Houston", "Phoenix", "Philadelphia", "San Antonio", "San Diego", "Dallas", "San Jose",
    "Austin", "Jacksonville", "Fort Worth", "Columbus", "Charlotte", "San Francisco", "Indianapolis", "Seattle", "Denver", "Washington",
    "Boston", "El Paso", "Nashville", "Detroit", "Oklahoma City", "Portland", "Las Vegas", "Memphis", "Louisville", "Baltimore",
    "Milwaukee", "Albuquerque", "Tucson", "Fresno", "Sacramento", "Kansas City", "Mesa", "Atlanta", "Omaha", "Colorado Springs",
    "Raleigh", "Miami", "Long Beach", "Virginia Beach", "Oakland", "Minneapolis", "Tulsa", "Arlington", "Tampa", "New Orleans",
    "Newark", "Jersey City", "Plano", "Anaheim", "Orlando", "Irvine", "Buffalo", "Cincinnati", "Pittsburgh", "St. Louis",
  ],
  "Canada": [
    "Toronto", "Montreal", "Vancouver", "Calgary", "Edmonton", "Ottawa", "Mississauga", "Winnipeg", "Quebec City", "Hamilton",
    "Brampton", "Surrey", "Kitchener", "Laval", "Halifax", "London (ON)", "Markham", "Vaughan", "Gatineau", "Saskatoon",
    "Longueuil", "Burnaby", "Regina", "Richmond (BC)", "Richmond Hill", "Oakville", "Burlington", "Greater Sudbury", "Sherbrooke", "Oshawa",
    "Saguenay", "Lévis", "Barrie", "Abbotsford", "Coquitlam", "Trois-Rivières", "St. Catharines", "Guelph", "Cambridge (ON)", "Whitby",
  ],
  "United Arab Emirates": [
    "Dubai", "Abu Dhabi", "Sharjah", "Al Ain", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain", "Khor Fakkan", "Kalba",
    "Dibba Al-Fujairah", "Dibba Al-Hisn", "Madinat Zayed", "Ruwais", "Ghayathi", "Liwa Oasis",
  ],
  "Saudi Arabia": [
    "Riyadh", "Jeddah", "Mecca", "Medina", "Dammam", "Khobar", "Dhahran", "Tabuk", "Buraidah", "Khamis Mushait",
    "Abha", "Najran", "Yanbu", "Al Hofuf", "Ta'if", "Hail", "Jubail", "Al-Kharj", "Arar", "Sakaka",
    "Jizan", "Qatif", "Bisha", "Al-Qunfudhah", "Al-Bahah",
  ],
  "Australia": [
    "Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Gold Coast", "Newcastle", "Canberra", "Sunshine Coast", "Wollongong",
    "Hobart", "Geelong", "Townsville", "Cairns", "Darwin", "Toowoomba", "Ballarat", "Bendigo", "Albury–Wodonga", "Launceston",
    "Mackay", "Rockhampton", "Bunbury", "Coffs Harbour", "Wagga Wagga",
  ],
  "Germany": [
    "Berlin", "Hamburg", "Munich", "Cologne", "Frankfurt", "Stuttgart", "Düsseldorf", "Leipzig", "Dortmund", "Essen",
    "Bremen", "Dresden", "Hanover", "Nuremberg", "Duisburg", "Bochum", "Wuppertal", "Bielefeld", "Bonn", "Münster",
    "Karlsruhe", "Mannheim", "Augsburg", "Wiesbaden", "Mönchengladbach", "Gelsenkirchen", "Braunschweig", "Chemnitz", "Kiel", "Aachen",
  ],
  "Qatar": [
    "Doha", "Al Wakrah", "Al Khor", "Al Rayyan", "Umm Salal", "Dukhan", "Mesaieed", "Lusail", "Madinat ash Shamal",
  ],
  "Kuwait": [
    "Kuwait City", "Hawalli", "Salmiya", "Farwaniya", "Jahra", "Mubarak Al-Kabeer", "Al Ahmadi", "Al Fintas", "Mahboula", "Mangaf",
  ],
  "Bahrain": [
    "Manama", "Muharraq", "Riffa", "Hamad Town", "A'ali", "Isa Town", "Sitra", "Budaiya", "Jidhafs", "Al Hidd",
  ],
  "Oman": [
    "Muscat", "Salalah", "Seeb", "Sohar", "Nizwa", "Sur", "Ibri", "Rustaq", "Buraimi", "Bahla",
  ],
  "Malaysia": [
    "Kuala Lumpur", "George Town", "Ipoh", "Shah Alam", "Petaling Jaya", "Johor Bahru", "Malacca", "Kota Kinabalu", "Kuching", "Klang",
    "Subang Jaya", "Kuantan", "Seremban", "Kota Bharu", "Alor Setar",
  ],
  "Singapore": [
    "Singapore", "Jurong East", "Tampines", "Woodlands", "Sengkang", "Hougang", "Bedok", "Yishun", "Ang Mo Kio", "Choa Chu Kang",
  ],
  "Turkey": [
    "Istanbul", "Ankara", "Izmir", "Bursa", "Adana", "Gaziantep", "Konya", "Antalya", "Kayseri", "Mersin",
    "Eskişehir", "Diyarbakir", "Samsun", "Denizli", "Şanlıurfa",
  ],
  "China": [
    "Beijing", "Shanghai", "Guangzhou", "Shenzhen", "Tianjin", "Chongqing", "Chengdu", "Wuhan", "Xi'an", "Hangzhou",
    "Nanjing", "Suzhou", "Qingdao", "Dalian", "Shenyang", "Harbin", "Jinan", "Changsha", "Zhengzhou", "Kunming",
  ],
  "India": [
    "Mumbai", "Delhi", "Bangalore", "Hyderabad", "Ahmedabad", "Chennai", "Kolkata", "Pune", "Jaipur", "Surat",
    "Lucknow", "Kanpur", "Nagpur", "Indore", "Thane", "Bhopal", "Visakhapatnam", "Patna", "Vadodara", "Ghaziabad",
  ],
  "France": [
    "Paris", "Marseille", "Lyon", "Toulouse", "Nice", "Nantes", "Strasbourg", "Montpellier", "Bordeaux", "Lille",
    "Rennes", "Reims", "Le Havre", "Saint-Étienne", "Toulon",
  ],
  "Italy": [
    "Rome", "Milan", "Naples", "Turin", "Palermo", "Genoa", "Bologna", "Florence", "Bari", "Catania",
    "Venice", "Verona", "Messina", "Padua", "Trieste",
  ],
  "Spain": [
    "Madrid", "Barcelona", "Valencia", "Seville", "Zaragoza", "Málaga", "Murcia", "Palma", "Las Palmas", "Bilbao",
    "Alicante", "Córdoba", "Valladolid", "Vigo", "Gijón",
  ],
  "Netherlands": [
    "Amsterdam", "Rotterdam", "The Hague", "Utrecht", "Eindhoven", "Tilburg", "Groningen", "Almere", "Breda", "Nijmegen",
    "Enschede", "Haarlem", "Arnhem", "Zaanstad", "Amersfoort",
  ],
  "Sweden": ["Stockholm", "Gothenburg", "Malmö", "Uppsala", "Västerås", "Örebro", "Linköping", "Helsingborg", "Jönköping", "Norrköping"],
  "Norway": ["Oslo", "Bergen", "Stavanger", "Trondheim", "Drammen", "Fredrikstad", "Kristiansand", "Sandnes", "Tromsø", "Sarpsborg"],
  "Denmark": ["Copenhagen", "Aarhus", "Odense", "Aalborg", "Esbjerg", "Randers", "Kolding", "Horsens", "Vejle", "Roskilde"],
  "Ireland": ["Dublin", "Cork", "Limerick", "Galway", "Waterford", "Drogheda", "Dundalk", "Swords", "Bray", "Navan"],
  "Belgium": ["Brussels", "Antwerp", "Ghent", "Charleroi", "Liège", "Bruges", "Namur", "Leuven", "Mons", "Mechelen"],
  "Switzerland": ["Zurich", "Geneva", "Basel", "Bern", "Lausanne", "Winterthur", "Lucerne", "St. Gallen", "Lugano", "Biel"],
  "Japan": [
    "Tokyo", "Yokohama", "Osaka", "Nagoya", "Sapporo", "Fukuoka", "Kobe", "Kyoto", "Kawasaki", "Saitama",
    "Hiroshima", "Sendai", "Chiba", "Kitakyushu", "Sakai",
  ],
  "South Korea": [
    "Seoul", "Busan", "Incheon", "Daegu", "Daejeon", "Gwangju", "Suwon", "Ulsan", "Goyang", "Yongin",
    "Changwon", "Seongnam", "Cheongju", "Bucheon", "Ansan",
  ],
  "Thailand": [
    "Bangkok", "Nonthaburi", "Pak Kret", "Hat Yai", "Chiang Mai", "Surat Thani", "Khon Kaen", "Nakhon Ratchasima", "Udon Thani", "Phuket",
  ],
  "Indonesia": [
    "Jakarta", "Surabaya", "Medan", "Bandung", "Bekasi", "Tangerang", "Depok", "Semarang", "Palembang", "Makassar",
  ],
  "Philippines": [
    "Manila", "Quezon City", "Davao City", "Cebu City", "Caloocan", "Zamboanga City", "Taguig", "Antipolo", "Pasig", "Cagayan de Oro",
  ],
  "Egypt": ["Cairo", "Alexandria", "Giza", "Shubra El Kheima", "Port Said", "Suez", "Luxor", "Mansoura", "El-Mahalla El-Kubra", "Tanta"],
  "South Africa": ["Johannesburg", "Cape Town", "Durban", "Pretoria", "Port Elizabeth", "Bloemfontein", "East London", "Polokwane", "Nelspruit", "Kimberley"],
  "Nigeria": ["Lagos", "Kano", "Ibadan", "Benin City", "Port Harcourt", "Abuja", "Kaduna", "Maiduguri", "Zaria", "Aba"],
  "Brazil": ["São Paulo", "Rio de Janeiro", "Brasília", "Salvador", "Fortaleza", "Belo Horizonte", "Manaus", "Curitiba", "Recife", "Porto Alegre"],
  "Mexico": ["Mexico City", "Guadalajara", "Monterrey", "Puebla", "Tijuana", "León", "Juárez", "Zapopan", "Querétaro", "Mérida"],
  "Argentina": ["Buenos Aires", "Córdoba", "Rosario", "Mendoza", "Tucumán", "La Plata", "Mar del Plata", "Salta", "Santa Fe", "San Juan"],
  "New Zealand": ["Auckland", "Wellington", "Christchurch", "Hamilton", "Tauranga", "Dunedin", "Palmerston North", "Napier", "Hastings", "Nelson"],
};

/** Returns the city list for a country (or empty array if unknown). */
export function getCitiesForCountry(country) {
  if (!country) return [];
  return CITIES[country] || [];
}

/** True when the country has a curated city list. */
export function hasCityList(country) {
  return !!CITIES[country];
}
